import {type RoomClientState} from '../../../../shared/scripts/RoomClientState.ts'
import {UIPopupController} from '../../../../shared/scripts/ui_popup/UIPopupController.ts'
import {MapDataController} from '../../../../../shared/map_data/MapDataController.ts'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables.ts'
import {UIComponentItemScroll} from '../../../../shared/scripts/ui_components/item_scroll/UIComponentItemScroll.ts'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState.ts'
import {type ItemData} from '../../../../shared/scripts/ui_components/item_scroll/UIComponentItemScrollCell.ts'
import {MapSettingEnum} from '@colonist/game-base-types'
import {type BaseMapData} from '../../../../../shared/map_data/BaseMapData.ts'
import {type UserState} from '../../../../../shared/UserState.ts'
import {createDiv, createImgIn, createParagraphIn} from '../../../../shared/scripts/ui_utils/DomUtils.ts'
import {type TranslationObject} from '@colonist/i18n'
import {clientI18n} from '../../../../shared/scripts/ClientI18n.ts'
import {ColonistMembershipPerkType} from '@colonist/lobby-types'
import {socket} from '../../../../shared/scripts/EntryShared.ts'

export class UIRoomCenterSettingsMapController {
    private readonly mapSelectionDiv: HTMLDivElement
    private mapSelector: UIComponentItemScroll

    private roomState: RoomClientState

    constructor() {
        this.mapSelectionDiv = document.getElementById('room_center_map_settings') as HTMLDivElement
    }

    update(newRoomState: RoomClientState) {
        const shouldUpdate = this.shouldUpdate(newRoomState)
        const shouldResetSelector = this.shouldResetMapSelector(newRoomState)
        this.roomState = newRoomState
        if(shouldUpdate) this.updateMapSelector()
        if(shouldResetSelector) this.resetMapSelector()
    }

    load(roomState: RoomClientState) {
        this.roomState = roomState
        if(this.mapSelector == undefined) {
            this.createMapSelector()
            return
        }
        this.updateMapSelector()
    }

    updateInteractabilityOfElements(isGuest: boolean) {
        this.mapSelector.updateInteractabilityOfElements(isGuest)
    }

    private createMapSelector() {
        const mapItemData = this.getMapItemData()
        this.mapSelector = new UIComponentItemScroll(this.mapSelectionDiv, mapItemData)
    }

    private updateMapSelector() {
        const mapItemData = this.getMapItemData()
        this.mapSelector.update(mapItemData)
    }

    private resetMapSelector() {
        this.mapSelector.resetScroll()
    }

    private getMapItemData(): ItemData[] {
        const compatibleMapData = MapDataController.getMapDataCompatibleWithGameMode(this.roomState.gameSetting.modeSetting)
        const userState = ClientUserState.userState
        const mapItemData: ItemData[] = []
        for(const mapData of compatibleMapData) {
            if(!mapData.isActive) continue

            const isMapAvailable = this.isMapAvailable(mapData, userState)
            const isMapSelected = this.isMapSelected(mapData)
            const clickAction = () => {
                socket.roomClient.setMap(mapData.mapEnum)
            }
            const displayInfoAction = () => {
                UIRoomCenterSettingsMapController.displayMapInfo(mapData)
            }

            const bannerText: TranslationObject | undefined = UIRoomCenterSettingsMapController.shouldShowWeeklyFreeBanner(mapData, userState)
                ? {key: 'strings:roomPage.options.map.weeklyFree'}
                : undefined

            const itemData: ItemData = {
                name: UIRoomCenterSettingsMapController.getMapName(mapData),
                image: mapData.image,
                available: isMapAvailable,
                selected: isMapSelected,
                bannerText: bannerText,
                clickAction: clickAction,
                infoAction: displayInfoAction,
                showInfoIcon: true,
            }
            mapItemData.push(itemData)
        }
        return mapItemData
    }

    private isMapAvailable(mapData: BaseMapData, userState: UserState | undefined): boolean {
        if(this.roomState.isTestFreeExpansionsAndMaps) return true
        if(this.roomState.isTournament) return true
        if(mapData.mapEnum == UIUtilsEjsVariables.freeMap) return true
        if(mapData.isMapPremium() == false) return true
        if(userState?.items.hasAccessToPremiumMap(mapData)) return true
        return false
    }

    private isMapSelected(mapData: BaseMapData): boolean {
        return mapData.mapEnum == this.roomState.gameSetting.mapSetting
    }

    private static shouldShowWeeklyFreeBanner(mapData: BaseMapData, userState: UserState | undefined): boolean {
        if(userState?.items.isPerkAvailable(ColonistMembershipPerkType.AllMaps)) return false
        if(mapData.mapEnum == UIUtilsEjsVariables.freeMap) return true
        return false
    }

    private shouldUpdate(newRoomState: RoomClientState): boolean {
        const didModeChange = this.roomState.gameSetting.modeSetting != newRoomState.gameSetting.modeSetting
        const didMapChange = this.roomState.gameSetting.mapSetting != newRoomState.gameSetting.mapSetting

        if(didModeChange) return true
        if(didMapChange) return true
        return false
    }

    private shouldResetMapSelector(newRoomState: RoomClientState): boolean {
        const didModeChange = this.roomState.gameSetting.modeSetting != newRoomState.gameSetting.modeSetting
        return didModeChange
    }

    private static displayMapInfo(mapData: BaseMapData) {
        const container = createDiv('room-map-preview-grid')
        createImgIn(container, mapData.image, 'room-item-popup-image room-item-map-popup-image')
        const mapInfoBody = UIRoomCenterSettingsMapController.getMapInfoBody(mapData)
        createParagraphIn(container, {key: 'strings:colonistCommon.utils.#buffer', options: {value: mapInfoBody}}, 'room-item-popup-text')

        const translatedPopupBody: TranslationObject = {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {
                value: container.outerHTML,
                interpolation: {escapeValue: false},
            },
        }
        UIPopupController.createSimplePopup(mapData.name, translatedPopupBody)
    }

    private static getMapInfoBody(mapData: BaseMapData): string {
        switch(mapData.mapEnum) {
            case MapSettingEnum.Classic4P: return clientI18n.t('strings:colonistCommon.gameMaps.classic.4P.description')
            case MapSettingEnum.Classic4PRandom: return clientI18n.tArray('strings:colonistCommon.gameMaps.classic.random4P.description').join('')
            case MapSettingEnum.SS1HeadingForNewShores3P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.headingForNewShores', {count: 3})
            case MapSettingEnum.SS1HeadingForNewShores4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.headingForNewShores', {count: 4})
            case MapSettingEnum.SS1HeadingForNewShores56P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.headingForNewShores', {count: 6})
            case MapSettingEnum.TutorialSS1HeadingForNewShores4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.tutorialHeadingForNewShores4P')
            case MapSettingEnum.SS2FourIslands3P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.fourIslands', {count: 3})
            case MapSettingEnum.SS2FourIslands4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.fourIslands', {count: 4})
            case MapSettingEnum.SS2SixIslands56P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.sixIslands')
            case MapSettingEnum.TutorialSS2FourIslands4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.tutorialFourIslands4P')
            case MapSettingEnum.SS3FogIslands3P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.fogIslands', {count: 3})
            case MapSettingEnum.SS3FogIslands4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.fogIslands', {count: 4})
            case MapSettingEnum.SS3FogIslands56P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.fogIslands', {count: 6})
            case MapSettingEnum.TutorialSS3FogIsland4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.tutorialFogIslands4P')
            case MapSettingEnum.SS4ThroughTheDesert3P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.throughTheDesert', {count: 3})
            case MapSettingEnum.SS4ThroughTheDesert4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.throughTheDesert', {count: 4})
            case MapSettingEnum.SS4ThroughTheDesert56P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.throughTheDesert', {count: 6})
            case MapSettingEnum.TutorialSS4ThroughTheDesert4P: return clientI18n.t('strings:colonistCommon.gameMaps.seafarers.tutorialThroughTheDesert4P')
            case MapSettingEnum.Earth: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.earth.description').join('')
            case MapSettingEnum.EarthSeafarers: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.earth.description').join('')
            case MapSettingEnum.USA: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.usa.description').join('')
            case MapSettingEnum.UK: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.uk.description').join('')
            case MapSettingEnum.UKSeafarers: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.uk.description').join('')
            case MapSettingEnum.Diamond: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.diamond.description').join('')
            case MapSettingEnum.Gear: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.gear.description').join('')
            case MapSettingEnum.Lakes: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.lakes.description').join('')
            case MapSettingEnum.Pond: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.pond.description').join('')
            case MapSettingEnum.Twirl: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.twirl.description').join('')
            case MapSettingEnum.ShuffleBoard: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.shuffleBoard.description').join('')
            case MapSettingEnum.BlackForest: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.blackForest.description').join('')
            case MapSettingEnum.Volcano: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.volcano.description').join('')
            case MapSettingEnum.GoldRush: return clientI18n.tArray('strings:colonistCommon.gameMaps.fun.goldRush.description').join('')
            default: return ''
        }
    }

    private static getMapName(mapData: BaseMapData): TranslationObject {
        switch(mapData.mapEnum) {
            case MapSettingEnum.Classic56P: return {key: 'strings:colonistCommon.gameMaps.classic.56P.abbreviation'}
            case MapSettingEnum.Classic78P: return {key: 'strings:colonistCommon.gameMaps.classic.78P.abbreviation'}
            default: return mapData.name
        }
    }
}
